import printStylesSource from '@/libs/printStyles.generated.js'

function resolvePrintStyles(styles) {
  if (typeof styles === 'string') {
    return styles
  }

  if (styles && typeof styles.default === 'string') {
    return styles.default
  }

  return ''
}

const printStyles = resolvePrintStyles(printStylesSource)

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function resolveStyleUrl(style) {
  if (/^https?:\/\//i.test(style) || style.startsWith('//')) {
    return style
  }

  return `${window.location.origin}${style.startsWith('/') ? style : `/${style}`}`
}

function injectInlineStyle(win, css) {
  if (!css || !String(css).trim()) {
    return
  }

  const styleEl = win.document.createElement('style')
  styleEl.type = 'text/css'
  styleEl.textContent = css
  win.document.head.appendChild(styleEl)
}

async function injectExternalStyles(win, styles) {
  await Promise.all(styles.map(async (style) => {
    try {
      const response = await fetch(resolveStyleUrl(style), { credentials: 'same-origin' })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      injectInlineStyle(win, await response.text())
    } catch (error) {
      console.error(`Failed to load print stylesheet: ${style}`, error)
    }
  }))
}

function waitForImages(win) {
  const images = Array.from(win.document.images || [])

  if (!images.length) {
    return Promise.resolve()
  }

  return Promise.all(images.map((image) => {
    if (image.complete) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      image.onload = resolve
      image.onerror = resolve
    })
  }))
}

const VueHtmlToPaper = {
  install(app, options = {}) {
    app.config.globalProperties.$htmlToPaper = async (
      el,
      localOptions,
      cb = () => true,
    ) => {
      const defaultName = '_blank'
      const defaultSpecs = ['fullscreen=yes', 'titlebar=yes', 'scrollbars=yes']
      const defaultReplace = true
      const defaultStyles = []

      let {
        name = defaultName,
        specs = defaultSpecs,
        replace = defaultReplace,
        styles = defaultStyles,
      } = options

      if (localOptions && typeof localOptions === 'object') {
        if (localOptions.name) name = localOptions.name
        if (localOptions.specs) specs = localOptions.specs
        if (localOptions.replace) replace = localOptions.replace
        if (localOptions.styles) styles = localOptions.styles
      }

      specs = specs.length ? specs.join(',') : ''

      const element = window.document.getElementById(el)

      if (!element) {
        console.error(`Element to print #${el} not found`)
        cb()
        return false
      }

      const win = window.open('', name, specs, replace)

      if (!win) {
        console.error('Print window blocked by the browser')
        cb()
        return false
      }

      const isLabelPrint = element.classList.contains('print-label-sheet')
      const pageTitle = isLabelPrint
        ? '\u00A0'
        : (localOptions && typeof localOptions === 'object' && localOptions.title
          ? localOptions.title
          : window.document.title)
      const bodyClass = isLabelPrint ? 'print-label-document' : ''

      if (!printStyles) {
        console.error('Print styles are missing. Run: node scripts/generate-print-styles.js')
      }

      win.document.open()
      win.document.write(`
        <html>
          <head>
            <title>${escapeHtml(pageTitle)}</title>
            <style>${printStyles}</style>
          </head>
          <body class="${bodyClass}">
            ${element.outerHTML}
          </body>
        </html>
      `)
      win.document.close()

      try {
        await injectExternalStyles(win, styles)
        await waitForImages(win)

        await new Promise((resolve) => {
          const cleanup = () => {
            win.removeEventListener('afterprint', cleanup)
            if (!win.closed) {
              win.close()
            }
            resolve()
          }

          win.addEventListener('afterprint', cleanup)
          win.focus()
          win.print()

          // Fallback if afterprint never fires (some browsers).
          setTimeout(cleanup, 120000)
        })
      } catch (error) {
        console.error('Print failed', error)
        if (!win.closed) {
          win.close()
        }
      }

      cb()
      return true
    }
  },
}

export default VueHtmlToPaper
